import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { So5LeaderboardSeasonality } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { SportSeasonalityIcon } from '@sorare/core/src/components/SportSeasonalityIcon';
import { useConfigContext } from '@sorare/core/src/contexts/config';
import type { Color } from '@sorare/core/src/style/types';

import type { FixtureCompetitionTabs_so5LeagueTrack } from './__generated__/index.graphql';

type Props = {
  trck?: string;
  so5LeagueTracks: FixtureCompetitionTabs_so5LeagueTrack[];
  onClick: (args: { trck: string }) => void;
};

export const FixtureCompetitionTabs = ({
  trck,
  so5LeagueTracks,
  onClick,
}: Props) => {
  const {
    so5: { inSeasonName, allSeasonsName },
  } = useConfigContext();

  if (so5LeagueTracks.length < 2) return null;

  return (
    <TabBar forceMobileLayout variant="primary" fullWidth value={trck}>
      {so5LeagueTracks.map(so5LeagueTrck => {
        const selected = trck === so5LeagueTrck.slug;
        let iconColor: Color | undefined = selected
          ? 'var(--c-black)'
          : 'var(--c-nd-600)';
        if (
          so5LeagueTrck.seasonality === So5LeaderboardSeasonality.IN_SEASON &&
          !selected
        ) {
          iconColor = undefined;
        }

        return (
          <Tab
            key={so5LeagueTrck.slug}
            value={so5LeagueTrck.slug}
            onClick={() => {
              onClick({
                trck: so5LeagueTrck.slug,
              });
            }}
            replace
          >
            <Horizontal>
              {so5LeagueTrck.seasonality ? (
                <>
                  <SportSeasonalityIcon
                    seasonality={so5LeagueTrck.seasonality}
                    color={iconColor}
                  />
                  {so5LeagueTrck.seasonality ===
                  So5LeaderboardSeasonality.IN_SEASON
                    ? inSeasonName
                    : allSeasonsName}
                </>
              ) : (
                so5LeagueTrck.displayName
              )}
            </Horizontal>
          </Tab>
        );
      })}
    </TabBar>
  );
};

FixtureCompetitionTabs.fragments = {
  so5LeagueTrack: gql`
    fragment FixtureCompetitionTabs_so5LeagueTrack on So5LeagueTrack {
      slug
      displayName
      seasonality
    }
  ` as TypedDocumentNode<FixtureCompetitionTabs_so5LeagueTrack>,
};
